import { createContext, useContext, useCallback, useState } from "react";
import api from "../lib/api";

const LanguageContext = createContext(null);

// Static UI dictionary
const DICT = {
  en: {
    home: "Home",
    video: "Video",
    presentations: "Presentations",
    other: "Other Content",
    search: "Search",
    admin: "Admin",
    login: "Login",
    logout: "Logout",
    enter_academy: "Enter the Academy",
    browse_library: "Browse Library",
    view_program: "View Program",
    hero_kicker: "Advanced Course on Abdominal Wall Reconstruction",
    hero_l1: "New Techniques",
    hero_l2: "& Innovations",
    hero_sub: "A digital library of surgical video, presentations and clinical material from the Wall Board Academy faculty. Learn the future of abdominal wall reconstruction.",
    manifesto_title: "The Method",
    ch1_t: "Diastasis",
    ch1_d: "Open vs. minimally invasive surgery. Pathophysiology, physical therapy and modern repair techniques including MILA and TESAR.",
    ch2_t: "Retromuscular & CS",
    ch2_d: "Minimally invasive retromuscular and component separation approaches: TARUP, eTEP, MILOS, ACS and TAR anatomy.",
    ch3_t: "Posterior & VFT",
    ch3_d: "Intraperitoneal / preperitoneal and VFT approaches. Loss of domain, Botox, Fasciotens and mesh selection.",
    library: "The Library",
    library_sub: "Every video, presentation and resource — searchable, alphabetically ordered.",
    all: "All",
    search_placeholder: "Search all content...",
    no_results: "No content found.",
    speaker: "Speaker",
    watch: "Watch",
    open_file: "Open file",
    download: "Download",
    back_to_library: "Back to library",
    admin_dashboard: "Admin Dashboard",
    add_content: "Add Content",
    edit: "Edit",
    delete: "Delete",
    save: "Save",
    cancel: "Cancel",
    title: "Title",
    description: "Description",
    category: "Category",
    vimeo_url: "Vimeo URL",
    upload_file: "Upload File (PPTX / MP4)",
    manage_content: "Manage all videos, presentations and resources.",
    total_items: "Total items",
    sign_in: "Sign in",
    admin_access: "Admin Access",
    email: "Email",
    password: "Password",
    footer_tag: "Under the patronage of SIC, SICE, ISHAWS.",
    organized: "Organized by Federico Fiori MD — Head of Abdominal Wall Surgery Unit, ASST Santi Paolo e Carlo — Milano",
    empty_section: "No items in this section yet.",
    program: "Preliminary Program",
    program_title: "Preliminary Program",
    program_sub: "An advanced, hands-on journey through modern abdominal wall reconstruction — three focus sessions, live surgery and international faculty.",
    faculty: "Faculty",
    registration: "Registration",
    venue: "Venue",
    fee_includes: "The fee includes",
    course_directors: "Course Directors",
    scientific_committee: "Scientific Committee",
    patronage: "Under the patronage of",
    step: "Step",
    secretariat: "Organizing Secretariat",
    register_now: "Register Now",
    download_program: "Download Program",
    register: "Register",
    register_title: "Register",
    register_sub: "Fill in the form to access the video library, presentations and resources. You will immediately receive a generated password to sign in.",
    first_name: "First name",
    last_name: "Last name",
    birth_date: "Date of birth",
    institution: "Institution / Affiliation",
    specialization: "Specialization",
    optional: "optional",
    submit_request: "Register",
    already_account: "Already have an account?",
    no_account: "Don't have an account yet?",
    request_here: "Register here",
    login_here: "Sign in",
    privacy_accept: "I have read and accept the",
    privacy_policy: "Privacy Policy",
    cookie_title: "Cookies & Privacy",
    cookie_message: "We use only strictly necessary cookies to make the site work, plus one anonymous analytics cookie (PostHog) to improve the product. No advertising, no third-party tracking.",
    cookie_accept: "Accept all",
    cookie_reject: "Only necessary",
    cookie_more: "Read Privacy Policy",
    members_only_title: "Members only",
    members_only_sub: "Please sign in to access this content. If you don't have an account, register.",
    supported_by: "Supported by",
    supported_sub: "Content and materials provided by our partners and sponsors.",
    registrations: "Registrations",
    approve: "Approve",
    status: "Status",
    pending: "Pending",
    approved: "Approved",
    rejected: "Rejected",
    sponsor_page: "Sponsor page",
    sponsor_toggle_hint: "Show the \"Supported by\" page on the site",
    register_success_title: "Registration complete",
    register_success_msg: "Your account is active. Here are your credentials:",
    register_save_warning: "Save your password now: for security reasons it will not be shown again. If you lose it, use \"Forgot password\" on the sign-in page.",
    your_password: "Password",
    copy: "Copy",
    copied: "Copied!",
    go_to_login: "Go to sign in",
    forgot_password: "Forgot password?",
    forgot_title: "Reset password",
    forgot_sub: "Enter your email and we'll send you a link to reset your password.",
    send_reset_link: "Send reset link",
    reset_sent_title: "Check your inbox",
    reset_sent_msg: "If an account exists for that email, you'll receive a link to reset your password shortly.",
    reset_title: "Set a new password",
    new_password: "New password",
    reset_password_btn: "Update password",
    reset_success_msg: "Your password has been updated. You can now sign in.",
    back_to_login: "Back to sign in",
    export_csv: "Export CSV",
    sponsor_website: "Sponsor website (URL)",
    sponsor_logo: "Sponsor logo",
    upload_logo: "Upload logo image",
    partners: "Our Partners",
    visit_website: "Visit website",
    account: "Account",
    my_account: "My Account",
    change_password: "Change password",
    current_password: "Current password",
    update_password: "Update password",
    password_changed: "Password updated successfully",
    statistics: "Statistics",
    members_label: "Members",
    views_label: "Total views",
    most_viewed: "Most viewed",
    send_reminder: "Send reminder",
    reminder_sent: "Reminder sent",
    no_views_yet: "No views recorded yet.",
    featured_label: "Featured",
    send_report: "Send monthly report",
    report_sent: "Monthly report sent",
    send_digest: "Email new content",
    digest_sent: "Email sent to {n} members · {c} new items",
    digest_none: "No new content since the last send",
    digest_preview_title: "New content email",
    digest_preview_sub: "The email will include these {c} new items:",
    digest_members_note: "The email will be sent to all approved members.",
    digest_send_now: "Send now",
    digest_last_sent: "Last sent:",
    digest_never_sent: "First send — includes all existing content",
    storage_title: "Storage space",
    storage_hint: "Files uploaded to this site · 5 GB Emergent quota",
    profile_saved: "Profile updated successfully",
    topics: "Topics",
    sort_by: "Sort",
    sort_default: "Featured",
    sort_recent: "Most recent",
    sort_views: "Most viewed",
    sort_az: "A-Z",
  },
  it: {
    home: "Home",
    video: "Video",
    presentations: "Presentazioni",
    other: "Altri Contenuti",
    search: "Cerca",
    admin: "Admin",
    login: "Accedi",
    logout: "Esci",
    enter_academy: "Entra nell'Academy",
    browse_library: "Esplora la Libreria",
    view_program: "Vedi il Programma",
    hero_kicker: "Corso Avanzato sulla Ricostruzione della Parete Addominale",
    hero_l1: "Nuove Tecniche",
    hero_l2: "& Innovazioni",
    hero_sub: "Una libreria digitale di video chirurgici, presentazioni e materiale clinico della faculty della Wall Board Academy. Impara il futuro della ricostruzione della parete addominale.",
    manifesto_title: "Il Metodo",
    ch1_t: "Diastasi",
    ch1_d: "Chirurgia aperta vs. mininvasiva. Fisiopatologia, fisioterapia e tecniche di riparazione moderne inclusi MILA e TESAR.",
    ch2_t: "Retromuscolare & CS",
    ch2_d: "Approcci mininvasivi retromuscolari e di separazione delle componenti: TARUP, eTEP, MILOS, ACS e anatomia TAR.",
    ch3_t: "Posteriore & VFT",
    ch3_d: "Approcci intraperitoneali / preperitoneali e VFT. Loss of domain, Botox, Fasciotens e scelta della mesh.",
    library: "La Libreria",
    library_sub: "Ogni video, presentazione e risorsa — ricercabile, in ordine alfabetico.",
    all: "Tutti",
    search_placeholder: "Cerca in tutti i contenuti...",
    no_results: "Nessun contenuto trovato.",
    speaker: "Relatore",
    watch: "Guarda",
    open_file: "Apri file",
    download: "Scarica",
    back_to_library: "Torna alla libreria",
    admin_dashboard: "Pannello Admin",
    add_content: "Aggiungi Contenuto",
    edit: "Modifica",
    delete: "Elimina",
    save: "Salva",
    cancel: "Annulla",
    title: "Titolo",
    description: "Descrizione",
    category: "Categoria",
    vimeo_url: "URL Vimeo",
    upload_file: "Carica File (PPTX / MP4)",
    manage_content: "Gestisci tutti i video, le presentazioni e le risorse.",
    total_items: "Elementi totali",
    sign_in: "Accedi",
    admin_access: "Accesso Admin",
    email: "Email",
    password: "Password",
    footer_tag: "Sotto il patrocinio di SIC, SICE, ISHAWS.",
    organized: "Organizzato da Federico Fiori MD — Responsabile Unità di Chirurgia della Parete Addominale, ASST Santi Paolo e Carlo — Milano",
    empty_section: "Ancora nessun elemento in questa sezione.",
    program: "Programma Preliminare",
    program_title: "Programma Preliminare",
    program_sub: "Un percorso avanzato e pratico nella ricostruzione moderna della parete addominale — tre sessioni focus, chirurgia dal vivo e faculty internazionale.",
    faculty: "Faculty",
    registration: "Iscrizione",
    venue: "Sede",
    fee_includes: "La quota include",
    course_directors: "Direttori del Corso",
    scientific_committee: "Comitato Scientifico",
    patronage: "Con il patrocinio di",
    step: "Step",
    secretariat: "Segreteria Organizzativa",
    register_now: "Iscriviti Ora",
    download_program: "Scarica il Programma",
    register: "Registrati",
    register_title: "Registrati",
    register_sub: "Compila il modulo per accedere alla libreria video, alle presentazioni e ai contenuti. Riceverai subito una password generata per accedere.",
    first_name: "Nome",
    last_name: "Cognome",
    birth_date: "Data di nascita",
    institution: "Ente di appartenenza",
    specialization: "Specializzazione",
    optional: "facoltativo",
    submit_request: "Registrati",
    already_account: "Hai già un account?",
    no_account: "Non hai ancora un account?",
    request_here: "Registrati qui",
    login_here: "Accedi",
    privacy_accept: "Ho letto e accetto la",
    privacy_policy: "Privacy Policy",
    cookie_title: "Cookie & Privacy",
    cookie_message: "Utilizziamo solo cookie strettamente necessari per il funzionamento del sito, più un cookie di analisi anonima (PostHog) per migliorare il prodotto. Nessuna pubblicità, nessun tracciamento di terze parti.",
    cookie_accept: "Accetta tutti",
    cookie_reject: "Solo necessari",
    cookie_more: "Leggi Privacy Policy",
    members_only_title: "Area riservata",
    members_only_sub: "Accedi per visualizzare questo contenuto. Se non hai un account, registrati.",
    supported_by: "Supported by",
    supported_sub: "Contenuti e materiali forniti dai nostri partner e sponsor.",
    registrations: "Iscrizioni",
    approve: "Approva",
    status: "Stato",
    pending: "In attesa",
    approved: "Approvato",
    rejected: "Rifiutato",
    sponsor_page: "Pagina sponsor",
    sponsor_toggle_hint: "Mostra la pagina \"Supported by\" sul sito",
    register_success_title: "Registrazione completata",
    register_success_msg: "Il tuo account è attivo. Ecco le tue credenziali:",
    register_save_warning: "Salva subito la password: per motivi di sicurezza non verrà mostrata di nuovo. Se la perdi, usa \"Password dimenticata\" nella pagina di accesso.",
    your_password: "Password",
    copy: "Copia",
    copied: "Copiata!",
    go_to_login: "Vai al login",
    forgot_password: "Password dimenticata?",
    forgot_title: "Reimposta password",
    forgot_sub: "Inserisci la tua email e ti invieremo un link per reimpostare la password.",
    send_reset_link: "Invia link di reset",
    reset_sent_title: "Controlla la tua email",
    reset_sent_msg: "Se esiste un account con quella email, riceverai a breve un link per reimpostare la password.",
    reset_title: "Imposta una nuova password",
    new_password: "Nuova password",
    reset_password_btn: "Aggiorna password",
    reset_success_msg: "La tua password è stata aggiornata. Ora puoi accedere.",
    back_to_login: "Torna al login",
    export_csv: "Esporta CSV",
    sponsor_website: "Sito dello sponsor (URL)",
    sponsor_logo: "Logo sponsor",
    upload_logo: "Carica immagine logo",
    partners: "I Nostri Partner",
    visit_website: "Visita il sito",
    account: "Account",
    my_account: "Il Mio Account",
    change_password: "Cambia password",
    current_password: "Password attuale",
    update_password: "Aggiorna password",
    password_changed: "Password aggiornata con successo",
    statistics: "Statistiche",
    members_label: "Iscritti",
    views_label: "Visualizzazioni totali",
    most_viewed: "Più visti",
    send_reminder: "Invia promemoria",
    reminder_sent: "Promemoria inviato",
    no_views_yet: "Nessuna visualizzazione registrata.",
    featured_label: "In evidenza",
    send_report: "Invia report mensile",
    report_sent: "Report mensile inviato",
    send_digest: "Invia mail nuovi contenuti",
    digest_sent: "Email inviata a {n} iscritti · {c} nuovi contenuti",
    digest_none: "Nessun nuovo contenuto dall'ultimo invio",
    digest_preview_title: "Email nuovi contenuti",
    digest_preview_sub: "L'email includerà questi {c} nuovi contenuti:",
    digest_members_note: "L'email verrà inviata a tutti gli iscritti approvati.",
    digest_send_now: "Invia ora",
    digest_last_sent: "Ultimo invio:",
    digest_never_sent: "Primo invio — include tutti i contenuti esistenti",
    storage_title: "Spazio archiviazione",
    storage_hint: "File caricati su questo sito · quota Emergent 5 GB",
    profile_saved: "Profilo aggiornato con successo",
    topics: "Argomenti",
    sort_by: "Ordina",
    sort_default: "In evidenza",
    sort_recent: "Più recenti",
    sort_views: "Più visti",
    sort_az: "A-Z",
  },
};

const cache = new Map(); // `${text}` -> italian

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("wba_lang") || "en");

  const toggle = () => {
    setLang((prev) => {
      const next = prev === "en" ? "it" : "en";
      localStorage.setItem("wba_lang", next);
      return next;
    });
  };

  const t = useCallback((key) => DICT[lang][key] ?? DICT.en[key] ?? key, [lang]);

  // Translate an array of dynamic strings (content titles/descriptions) EN->IT.
  const translateBatch = useCallback(
    async (texts) => {
      if (lang === "en") return texts;
      const toFetch = [];
      const idxMap = [];
      texts.forEach((txt, i) => {
        if (txt && !cache.has(txt)) {
          toFetch.push(txt);
          idxMap.push(i);
        }
      });
      if (toFetch.length) {
        try {
          const { data } = await api.post("/translate", { texts: toFetch, target: "it" });
          (data.translations || []).forEach((tr, j) => cache.set(toFetch[j], tr));
        } catch (e) {
          toFetch.forEach((txt) => cache.set(txt, txt));
        }
      }
      return texts.map((txt) => (txt ? cache.get(txt) || txt : txt));
    },
    [lang]
  );

  return (
    <LanguageContext.Provider value={{ lang, toggle, t, translateBatch }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    // Fallback for HMR edge cases / components rendered before provider mounts
    return { t: (k) => k, lang: "en", toggle: () => {}, translateBatch: async (arr) => arr };
  }
  return ctx;
};
